import { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  UserIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShoppingBagIcon,
  CurrencyEuroIcon,
  XMarkIcon,
  PencilSquareIcon,
  CheckIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import { ecommerceApi, organizationsApi } from '@/services/api';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '@/components/common';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Customer {
  _id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  total_orders: number;
  total_spent: number;
  currency: string;
  notes?: string;
  tags: string[];
  last_order_at?: string;
  created_at: string;
  updated_at: string;
}

interface CustomerDetail extends Customer {
  recent_orders: Array<{
    _id: string;
    order_number: string;
    status: string;
    totals: { total: number; currency?: string };
    created_at: string;
  }>;
}

interface SyncStatus {
  customerCount: number;
  lastSyncs: Array<{
    timestamp: string;
    synced: number;
    created: number;
    updated: number;
    errors: number;
  }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fullName(c: Customer) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return name || c.email;
}

function formatCurrency(amount: number, currency = 'EUR') {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(amount);
}

function formatDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

const ORDER_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

function CustomerDetailPanel({
  customer,
  canEdit,
  onClose,
  onSaved,
}: {
  customer: CustomerDetail;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (updated: Customer) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: customer.first_name || '',
    last_name: customer.last_name || '',
    phone: customer.phone || '',
    notes: customer.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await ecommerceApi.updateCustomer(customer._id, form);
      onSaved(res.data);
      setEditing(false);
      toast.success('Cliente actualizado');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Detalle del cliente</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* Avatar + email */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-vida-100 flex items-center justify-center text-vida-600 text-xl font-semibold">
              {(customer.first_name?.[0] || customer.email[0]).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-lg">{fullName(customer)}</p>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <EnvelopeIcon className="w-4 h-4" />
                {customer.email}
              </p>
              {customer.phone && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <PhoneIcon className="w-4 h-4" />
                  {customer.phone}
                </p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <ShoppingBagIcon className="w-3.5 h-3.5" />
                Pedidos
              </p>
              <p className="text-2xl font-bold text-gray-900">{customer.total_orders}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <CurrencyEuroIcon className="w-3.5 h-3.5" />
                Total gastado
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(customer.total_spent, customer.currency)}
              </p>
            </div>
          </div>

          {/* Editable fields */}
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.first_name}
                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Apellidos</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.last_name}
                    onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50"
                >
                  <CheckIcon className="w-4 h-4" />
                  Guardar
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {customer.notes && (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-sm text-gray-700">
                  {customer.notes}
                </div>
              )}
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 text-sm text-vida-600 hover:text-vida-700"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  Editar datos
                </button>
              )}
            </div>
          )}

          {/* Tags */}
          {customer.tags.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Etiquetas</p>
              <div className="flex flex-wrap gap-1.5">
                {customer.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="text-xs text-gray-500 space-y-1">
            <p>Primer pedido: {formatDate(customer.created_at)}</p>
            <p>Último pedido: {formatDate(customer.last_order_at)}</p>
          </div>

          {/* Recent orders */}
          {customer.recent_orders.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Últimos pedidos</p>
              <div className="space-y-2">
                {customer.recent_orders.map((order) => (
                  <div
                    key={order._id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm"
                  >
                    <div>
                      <p className="font-medium text-gray-900">#{order.order_number}</p>
                      <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${ORDER_STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-600'}`}
                      >
                        {order.status}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(order.totals.total, order.totals.currency)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CustomersPage() {
  const { hasPermission } = useAuthStore();
  const canRead = hasPermission(PERMISSIONS.ECOMMERCE_READ);
  const canEdit = hasPermission(PERMISSIONS.ECOMMERCE_UPDATE);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [filterHasOrders, setFilterHasOrders] = useState<boolean | undefined>(undefined);

  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Create customer
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', first_name: '', last_name: '', phone: '', notes: '' });
  const [creating, setCreating] = useState(false);

  const limit = 20;

  const load = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const res = await ecommerceApi.getCustomers(page, limit, {
        search: search || undefined,
        hasOrders: filterHasOrders,
      });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Error cargando clientes');
    } finally {
      setLoading(false);
    }
  }, [canRead, page, search, filterHasOrders]);

  useEffect(() => {
    load();
  }, [load]);

  // Load sync status for the first org the user belongs to
  useEffect(() => {
    const loadSyncStatus = async () => {
      try {
        const orgsRes = await organizationsApi.my();
        const orgs = orgsRes.data.organizations || [];
        if (orgs.length > 0) {
          const statusRes = await organizationsApi.getCustomersSyncStatus(orgs[0]._id);
          setSyncStatus(statusRes.data);
        }
      } catch {
        // non-critical
      }
    };
    loadSyncStatus();
  }, []);

  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const orgsRes = await organizationsApi.my();
      const orgs = orgsRes.data.organizations || [];
      if (orgs.length === 0) {
        toast.error('No hay organización asignada');
        return;
      }
      await organizationsApi.syncCustomers(orgs[0]._id);
      toast.success('Sincronización completada');
      load();
    } catch {
      toast.error('Error en la sincronización');
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await ecommerceApi.getCustomer(id);
      setSelectedCustomer(res.data);
    } catch {
      toast.error('Error cargando detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!createForm.email) { toast.error('Email es obligatorio'); return; }
    setCreating(true);
    try {
      await ecommerceApi.createCustomer(createForm);
      toast.success('Cliente creado');
      setShowCreate(false);
      setCreateForm({ email: '', first_name: '', last_name: '', phone: '', notes: '' });
      load();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Error al crear cliente';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleCustomerSaved = (updated: Customer) => {
    setCustomers((prev) => prev.map((c) => (c._id === updated._id ? { ...c, ...updated } : c)));
    if (selectedCustomer?._id === updated._id) {
      setSelectedCustomer((prev) => prev ? { ...prev, ...updated } : prev);
    }
  };

  if (!canRead) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No tienes permiso para ver los clientes.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {total} cliente{total !== 1 ? 's' : ''} registrado{total !== 1 ? 's' : ''}
            {syncStatus && (
              <span className="ml-3 text-gray-400">
                · Última sync: {syncStatus.lastSyncs[0] ? formatDate(syncStatus.lastSyncs[0].timestamp) : 'nunca'}
              </span>
            )}
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Crear cliente
            </button>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50 transition-colors"
            >
              <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              Sincronizar
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vida-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <FunnelIcon className="w-4 h-4 text-gray-400" />
          <select
            value={filterHasOrders === undefined ? '' : String(filterHasOrders)}
            onChange={(e) => {
              const v = e.target.value;
              setFilterHasOrders(v === '' ? undefined : v === 'true');
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-vida-500"
          >
            <option value="">Todos</option>
            <option value="true">Con pedidos</option>
            <option value="false">Sin pedidos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <UserIcon className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">No hay clientes todavía</p>
            {canEdit && (
              <p className="text-xs mt-1">Haz clic en «Sincronizar» para importar clientes desde pedidos</p>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-600">Cliente</th>
                <th className="px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Teléfono</th>
                <th className="px-4 py-3 font-medium text-gray-600 text-right">Pedidos</th>
                <th className="px-4 py-3 font-medium text-gray-600 text-right">Total gastado</th>
                <th className="px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Último pedido</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map((customer) => (
                <tr
                  key={customer._id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => openDetail(customer._id)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-vida-100 flex items-center justify-center text-vida-600 text-xs font-semibold flex-shrink-0">
                        {(customer.first_name?.[0] || customer.email[0]).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{fullName(customer)}</p>
                        <p className="text-xs text-gray-500 truncate">{customer.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                    {customer.phone || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {customer.total_orders}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {formatCurrency(customer.total_spent, customer.currency)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">
                    {formatDate(customer.last_order_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Página {page} de {pages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, pages))}
              disabled={page === pages}
              className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            >
              Siguiente
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {detailLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <LoadingSpinner />
        </div>
      )}
      {selectedCustomer && !detailLoading && (
        <CustomerDetailPanel
          customer={selectedCustomer}
          canEdit={canEdit}
          onClose={() => setSelectedCustomer(null)}
          onSaved={handleCustomerSaved}
        />
      )}

      {/* Create customer modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Nuevo cliente</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-vida-500 focus:border-vida-500"
                  placeholder="cliente@ejemplo.com"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    value={createForm.first_name}
                    onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-vida-500 focus:border-vida-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apellidos</label>
                  <input
                    type="text"
                    value={createForm.last_name}
                    onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-vida-500 focus:border-vida-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-vida-500 focus:border-vida-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-vida-500 focus:border-vida-500"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateCustomer}
                disabled={creating || !createForm.email}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {creating ? 'Creando...' : 'Crear cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
